import React, { useEffect, useState, useMemo, useRef } from "react";
import { ComposableMap, Geographies, Geography, Marker, Graticule, Sphere } from "react-simple-maps";
import api from "@/lib/api";
import { COUNTRY_BY_CODE } from "@/lib/countries";

// HD world atlas TopoJSON — countries-50m (5× the detail of the 110m file used for thumbnails).
// Small enough (~400 KB gzipped) to keep the homepage fast, sharp enough for premium retina displays.
const WORLD_TOPO = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";

// Higher-density viewBox for crisper anti-aliasing on retina + 4K displays.
const MAP_W = 1200;
const MAP_H = 560;
const MAP_SCALE = 210;

/**
 * Editorial world map showing the nationalities of couples Elite Pixels has captured.
 * Base state: subtle continents outlined in charcoal, gold ✓ over each represented country.
 * Hover state: the ✓ transforms into a small black-and-white couple portrait (if one is attached),
 * with the country name in editorial caps below — a living memory book.
 */
export default function GlobalLoveStories() {
  const [items, setItems] = useState([]);
  const [hovered, setHovered] = useState(null); // country code currently hovered / tapped
  const [scanLng, setScanLng] = useState(-180); // current meridian longitude (° -180..180)
  const containerRef = useRef(null);

  useEffect(() => {
    api.get("/couple-nationalities")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  // Meridian sweep — a slow-orbiting golden longitude line. 20 s per full revolution.
  // Pauses while the visitor is hovering a marker so the portrait card feels the focal point.
  useEffect(() => {
    if (hovered) return; // freeze the sweep while inspecting a country
    let raf;
    const PERIOD = 20000;
    const start = performance.now() - ((scanLng + 180) / 360) * PERIOD; // resume from where we left off
    const step = (now) => {
      const t = ((now - start) / PERIOD) % 1;
      setScanLng(-180 + t * 360);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered]);

  // Build the meridian polyline in SVG coordinates matching the ComposableMap projection.
  const meridianPath = useMemo(() => {
    const pts = [];
    for (let phi = -83; phi <= 83; phi += 2) {
      const p = projectEqualEarth(scanLng, phi);
      pts.push(`${p.x.toFixed(2)},${p.y.toFixed(2)}`);
    }
    return `M ${pts.join(" L ")}`;
  }, [scanLng]);

  const markers = useMemo(
    () =>
      items
        .map((it) => {
          const meta = COUNTRY_BY_CODE[(it.code || "").toUpperCase()];
          return meta
            ? { code: meta.code, name: meta.name, coord: meta.coord, photo_url: it.photo_url || null }
            : null;
        })
        .filter(Boolean),
    [items]
  );

  // Dismiss the hover portrait when tapping anywhere else (mobile UX)
  useEffect(() => {
    const onDocTap = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setHovered(null);
    };
    document.addEventListener("touchstart", onDocTap, { passive: true });
    return () => document.removeEventListener("touchstart", onDocTap);
  }, []);

  return (
    <section
      className="relative py-24 md:py-32 bg-black overflow-hidden"
      data-testid="global-love-stories-section"
      aria-label="Our Global Love Stories"
    >
      <div className="grain absolute inset-0 pointer-events-none opacity-40" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center">
          <div className="eyebrow" data-testid="global-love-stories-eyebrow">
            Cultures We've Captured
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 tracking-tight leading-tight">
            Our <em className="text-[#D4AF37] not-italic">Global</em> Love Stories
          </h2>
          <p className="mt-5 text-white/65 leading-relaxed max-w-2xl mx-auto italic font-serif">
            "Different cultures. Different traditions. The same love."
          </p>
        </div>

        {/* Map wrapper — relative so we can layer HTML portrait cards on top of SVG markers */}
        <div ref={containerRef} className="mt-16 md:mt-20 relative">
          {/* Ambient radial glow behind the map for depth (very subtle) */}
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: "radial-gradient(ellipse at center, rgba(212,175,55,0.05) 0%, rgba(0,0,0,0) 60%)" }} />

          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{ scale: MAP_SCALE }}
            width={MAP_W}
            height={MAP_H}
            style={{ width: "100%", height: "auto", display: "block" }}
            data-testid="global-love-stories-map"
          >
            {/* Gradient for the meridian sweep — fade top/bottom so it feels like a light beam, not a hard line */}
            <defs>
              <linearGradient id="ep-meridian-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#D4AF37" stopOpacity="0" />
                <stop offset="25%"  stopColor="#D4AF37" stopOpacity="0.55" />
                <stop offset="50%"  stopColor="#F5D97A" stopOpacity="0.9" />
                <stop offset="75%"  stopColor="#D4AF37" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
              </linearGradient>
              <filter id="ep-meridian-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.5" />
              </filter>
            </defs>

            {/* Sphere outline — a very faint golden horizon around the world for editorial polish */}
            <Sphere stroke="#D4AF37" strokeOpacity={0.14} strokeWidth={0.6} fill="transparent" />
            {/* Latitude/longitude grid — extremely subtle */}
            <Graticule stroke="#D4AF37" strokeOpacity={0.06} strokeWidth={0.4} step={[15, 15]} />

            <Geographies geography={WORLD_TOPO}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#232323"
                    stroke="#4d4d4d"
                    strokeWidth={0.45}
                    style={{
                      default: { outline: "none" },
                      hover:   { outline: "none", fill: "#232323" },
                      pressed: { outline: "none", fill: "#232323" },
                    }}
                  />
                ))
              }
            </Geographies>

            {/* Meridian sweep — soft golden light beam that orbits the globe every 20 s.
                Pauses when a visitor is hovering a country so it doesn't compete with the portrait card. */}
            <g data-testid="meridian-scan" style={{ pointerEvents: "none" }}>
              <path d={meridianPath} fill="none" stroke="url(#ep-meridian-grad)"
                    strokeWidth={4} strokeLinecap="round" filter="url(#ep-meridian-glow)" opacity={0.85} />
              <path d={meridianPath} fill="none" stroke="url(#ep-meridian-grad)"
                    strokeWidth={1.1} strokeLinecap="round" opacity={0.95} />
            </g>

            {markers.map((m) => {
              const active = hovered === m.code;
              return (
                <Marker
                  key={m.code}
                  coordinates={m.coord}
                  data-testid={`global-love-mark-${m.code}`}
                  onMouseEnter={() => setHovered(m.code)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setHovered((prev) => (prev === m.code ? null : m.code))}
                  style={{ cursor: m.photo_url ? "pointer" : "default" }}
                >
                  {/* Outer soft-glow ring — grows on hover */}
                  <circle
                    r={active ? 14 : 11}
                    fill="#D4AF37"
                    fillOpacity={0.10}
                    style={{ transition: "r 250ms ease" }}
                  />
                  {/* Mid ring */}
                  <circle
                    r={active ? 9 : 7.5}
                    fill="#D4AF37"
                    fillOpacity={0.22}
                    style={{ transition: "r 250ms ease" }}
                  />
                  {/* Solid gold disc with a fine dark ring for definition on retina */}
                  <circle
                    r={5.2}
                    fill="#E5C464"
                    stroke="#0a0a0a"
                    strokeWidth={0.6}
                  />
                  {/* Crisp ✓ glyph */}
                  <path
                    d="M -2.2 0.1 L -0.6 1.7 L 2.4 -1.5"
                    fill="none"
                    stroke="#0a0a0a"
                    strokeWidth={1.3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Marker>
              );
            })}
          </ComposableMap>

          {/* HTML overlay layer: absolute-positioned cards using the same
              geoEqualEarth projection math so they line up with the SVG markers.
              On hover: country name is ALWAYS shown; portrait is shown when a photo is attached. */}
          <div className="absolute inset-0 pointer-events-none">
            {markers.map((m) => {
              const pt = projectEqualEarth(m.coord[0], m.coord[1]);
              const leftPct = ((pt.x + MAP_W / 2) / MAP_W) * 100;
              const topPct  = ((pt.y + MAP_H / 2) / MAP_H) * 100;
              const active = hovered === m.code;
              if (!active) return null;
              return (
                <div
                  key={`card-${m.code}`}
                  data-testid={`global-love-card-${m.code}`}
                  className="absolute -translate-x-1/2 -translate-y-full pointer-events-none"
                  style={{
                    left: `${leftPct}%`,
                    top:  `${topPct}%`,
                    marginTop: "-14px",
                  }}
                >
                  <div className="flex flex-col items-center animate-[epMapPortraitFadeUp_240ms_ease-out_both]">
                    {m.photo_url && (
                      <div className="relative">
                        <div className="absolute -inset-1 bg-[#D4AF37]/20 blur-md rounded-full" />
                        <img
                          src={m.photo_url}
                          alt=""
                          className="relative w-16 h-16 md:w-20 md:h-20 rounded-full object-cover grayscale ring-2 ring-[#D4AF37]/70 shadow-[0_10px_30px_rgba(0,0,0,0.7)]"
                        />
                      </div>
                    )}
                    <div className={`${m.photo_url ? "mt-2" : ""} px-3 py-1 bg-black/85 border border-[#D4AF37]/30 backdrop-blur-sm shadow-[0_6px_18px_rgba(0,0,0,0.55)]`}>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] whitespace-nowrap"
                           data-testid={`global-love-caption-${m.code}`}>
                        {m.name}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-10 text-center text-xs tracking-[0.28em] uppercase text-white/40">
          {markers.length > 0
            ? `${markers.length} ${markers.length === 1 ? "culture" : "cultures"} · one universal language`
            : "One universal language"}
        </p>
        {markers.length > 0 && (
          <p className="mt-2 text-center text-[11px] tracking-[0.2em] uppercase text-white/25">
            Hover a ✓ to see the culture
          </p>
        )}
      </div>

      <style>{`
        @keyframes epMapPortraitFadeUp {
          from { opacity: 0; transform: translate(-50%, calc(-100% + 8px)); }
          to   { opacity: 1; transform: translate(-50%, -100%); }
        }
      `}</style>
    </section>
  );
}

/**
 * Minimal inline geoEqualEarth projection (same formula d3-geo uses at scale 1, translate 0,0).
 * We match ComposableMap's projectionConfig.scale so HTML overlay cards line up with SVG markers.
 * Input: lon/lat in degrees. Output: {x,y} in SVG units where the map is centered at (0,0).
 * Derived from d3-geo/src/projection/equalEarth.js (BSD-3, © Mike Bostock).
 */
function projectEqualEarth(lambdaDeg, phiDeg) {
  const A1 = 1.340264;
  const A2 = -0.081106;
  const A3 = 0.000893;
  const A4 = 0.003796;
  const M = Math.sqrt(3) / 2;
  const lambda = (lambdaDeg * Math.PI) / 180;
  const phi = (phiDeg * Math.PI) / 180;
  const l = Math.asin(M * Math.sin(phi));
  const l2 = l * l;
  const l6 = l2 * l2 * l2;
  const x = (lambda * Math.cos(l)) / (M * (A1 + 3 * A2 * l2 + l6 * (7 * A3 + 9 * A4 * l2)));
  const y = l * (A1 + A2 * l2 + l6 * (A3 + A4 * l2));
  return { x: x * MAP_SCALE, y: -y * MAP_SCALE };
}
